import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Sparkles, Clock, ArrowRight, Info, Zap, Calendar, Users } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useMediaCategories } from "@/hooks/useMediaCategories";

interface RecommendationMetadata {
  slug?: string;
  excerpt?: string;
  category?: string;
  coverImageUrl?: string;
  publishedAt?: Date | null;
  startDate?: Date;
  location?: string;
  eventType?: string;
  headline?: string;
  avatarUrl?: string;
  role?: string;
  company?: string;
}

interface UnifiedRecommendation {
  id: string;
  type: 'article' | 'event' | 'video' | 'person';
  title: string;
  score: number;
  reasoning: string;
  metadata: RecommendationMetadata;
  personalizationApplied: boolean;
  personalizationBoost: number;
  personalizationReasons: string[];
}

interface RecommendationResult {
  recommendations: UnifiedRecommendation[];
  contentBreakdown: Record<string, number>;
  personalizationApplied: boolean;
  profileConfidence: number;
  cached: boolean;
  generatedAt: string;
}

function RecommendationCardSkeleton() {
  return (
    <Card data-testid="skeleton-recommendation">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-16" />
        </div>
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-3/4 mt-1" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-4 w-full mb-2" />
        <Skeleton className="h-4 w-3/4" />
      </CardContent>
    </Card>
  );
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function AIRecommendations() {
  const { getCategoryLabel } = useMediaCategories();
  const { data, isLoading, error } = useQuery<RecommendationResult>({
    queryKey: ["/api/recommendations/content"],
  });

  if (error) {
    return null;
  }

  if (isLoading) {
    return (
      <section className="mb-8" data-testid="section-ai-recommendations">
        <div className="flex items-center gap-2 mb-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-secondary" />
            <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">
              Recommended for You
            </h2>
          </div>
          <Badge variant="secondary" className="no-default-active-elevate">
            AI-Powered
          </Badge>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <RecommendationCardSkeleton key={i} />
          ))}
        </div>
      </section>
    );
  }

  const recommendations = data?.recommendations || [];

  if (recommendations.length === 0) {
    return null;
  }

  const showLowConfidencePrompt = 
    recommendations.length > 0 && 
    data?.profileConfidence !== undefined && 
    data.profileConfidence < 0.5;

  return (
    <section className="mb-8" data-testid="section-ai-recommendations">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-2 flex-wrap">
          <Sparkles className="h-5 w-5 text-secondary" />
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground">
            Recommended for You
          </h2>
          <Badge variant="secondary" className="no-default-active-elevate">
            AI-Powered
          </Badge>
          {data?.personalizationApplied && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Badge 
                  variant="outline" 
                  className="flex items-center gap-1 cursor-help"
                  data-testid="badge-personalized"
                >
                  <Zap className="h-3 w-3" />
                  Personalized
                </Badge>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p className="text-sm">
                  Based on {Math.round((data?.profileConfidence ?? 0) * 100)}% profile confidence
                </p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <div className="flex flex-col items-start sm:items-end gap-2">
          <p className="text-muted-foreground text-sm">
            Personalized based on your profile and interests
          </p>
          {showLowConfidencePrompt && (
            <Link href="/settings/personalization">
              <Button 
                variant="outline" 
                size="sm"
                data-testid="button-improve-recommendations"
              >
                Improve recommendations
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {recommendations.slice(0, 5).map((rec) => {
          const scorePercentage = Math.round(rec.score * 100);
          let cardHref = '';
          let contentIcon = null;

          if (rec.type === 'article') {
            cardHref = `/articles/${rec.metadata.slug || rec.id}`;
            contentIcon = <Sparkles className="h-4 w-4" />;
          } else if (rec.type === 'event') {
            cardHref = `/events`;
            contentIcon = <Calendar className="h-4 w-4" />;
          } else if (rec.type === 'person') {
            cardHref = `/directory`;
            contentIcon = <Users className="h-4 w-4" />;
          } else if (rec.type === 'video') {
            cardHref = `/videos`;
            contentIcon = <Sparkles className="h-4 w-4" />;
          }

          return (
            <Link key={rec.id} href={cardHref}>
              <Card 
                className="h-full hover-elevate cursor-pointer" 
                data-testid={`card-recommendation-${rec.id}`}
              >
                {rec.type === 'person' && rec.metadata.avatarUrl && (
                  <div className="relative h-40 bg-gradient-to-b from-secondary/10 to-secondary/5">
                    <Avatar className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-20 w-20 border-2 border-background">
                      <AvatarImage src={rec.metadata.avatarUrl} alt={rec.title} />
                      <AvatarFallback>{getInitials(rec.title)}</AvatarFallback>
                    </Avatar>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge 
                        variant="secondary" 
                        className="no-default-active-elevate flex items-center gap-1" 
                        data-testid={`badge-rec-type-${rec.id}`}
                      >
                        {contentIcon}
                        <span className="text-xs capitalize">
                          {rec.type === 'article' ? 'Article' : rec.type === 'event' ? 'Event' : rec.type === 'person' ? 'Person' : 'Video'}
                        </span>
                      </Badge>
                      {rec.metadata.category && rec.type === 'article' && (
                        <Badge 
                          variant="secondary" 
                          className="no-default-active-elevate" 
                          data-testid={`badge-rec-category-${rec.id}`}
                        >
                          {getCategoryLabel(rec.metadata.category)}
                        </Badge>
                      )}
                      {rec.personalizationApplied && (
                        <Badge 
                          variant="outline"
                          className="flex items-center gap-1"
                          data-testid={`badge-rec-personalized-${rec.id}`}
                        >
                          <Zap className="h-3 w-3" />
                          <span className="text-xs">Personalized</span>
                        </Badge>
                      )}
                      {rec.type === 'event' && rec.metadata.startDate && (
                        <span className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(rec.metadata.startDate)}
                        </span>
                      )}
                    </div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div 
                          className="flex items-center gap-1 text-secondary cursor-help"
                          data-testid={`tooltip-trigger-${rec.id}`}
                        >
                          <span className="text-xs font-medium">{scorePercentage}% match</span>
                          <Info className="h-3 w-3" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent 
                        side="top" 
                        className="max-w-xs"
                        data-testid={`tooltip-content-${rec.id}`}
                      >
                        <div className="space-y-2">
                          <div>
                            <p className="text-sm font-medium">Why we recommended this</p>
                            <p className="text-sm text-muted-foreground">{rec.reasoning}</p>
                          </div>
                          {rec.personalizationReasons && rec.personalizationReasons.length > 0 && (
                            <div>
                              <p className="text-sm font-medium">Personalization factors</p>
                              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                                {rec.personalizationReasons.map((reason, idx) => (
                                  <li key={idx}>{reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <CardTitle 
                    className="text-lg leading-snug line-clamp-2" 
                    data-testid={`text-rec-title-${rec.id}`}
                  >
                    {rec.title}
                  </CardTitle>
                  {rec.type === 'person' && rec.metadata.headline && (
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {rec.metadata.headline}
                    </p>
                  )}
                  {rec.type === 'person' && rec.metadata.company && (
                    <p className="text-sm text-muted-foreground">
                      {rec.metadata.company}
                    </p>
                  )}
                  {rec.type === 'event' && rec.metadata.location && (
                    <p className="text-sm text-muted-foreground">
                      {rec.metadata.location}
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {rec.metadata.excerpt && (
                    <CardDescription 
                      className="text-base line-clamp-2" 
                      data-testid={`text-rec-excerpt-${rec.id}`}
                    >
                      {rec.metadata.excerpt}
                    </CardDescription>
                  )}
                  <div className="mt-4 flex items-center text-secondary font-medium text-sm">
                    {rec.type === 'person' ? 'View profile' : 'Learn more'}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
